import { useCallback, useEffect, useState } from 'react';
import { agentUsageService, AgentUsageSummary } from '../services/agentUsageService';

interface UseAgentLLMUsageState {
    data: AgentUsageSummary | null;
    loading: boolean;
    error: string | null;
}

/**
 * Fetches and caches per-agent LLM usage. The hook refetches whenever the chatbotId
 * or the date range changes, and exposes a `refresh()` callback so the UI can re-query
 * on demand (e.g. after the user toggles a model in the parent component).
 */
export function useAgentLLMUsage(chatbotId: string | undefined, from?: string, to?: string) {
    const [state, setState] = useState<UseAgentLLMUsageState>({
        data: null,
        loading: false,
        error: null,
    });

    const fetchUsage = useCallback(async () => {
        if (!chatbotId) {
            setState({ data: null, loading: false, error: null });
            return;
        }
        setState(prev => ({ ...prev, loading: true, error: null }));
        try {
            const data = await agentUsageService.getAgentUsage(chatbotId, from, to);
            setState({ data, loading: false, error: null });
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Unknown error';
            setState({ data: null, loading: false, error: message });
        }
    }, [chatbotId, from, to]);

    useEffect(() => {
        void fetchUsage();
    }, [fetchUsage]);

    return {
        ...state,
        refresh: fetchUsage,
    };
}
